/**
 * P45 Service
 * Handles P45 document generation and retrieval
 */

import bcClient from '../businessCentralClient';
import { BC_CONFIG } from '../config';

/**
 * Generate P45 document in base64 format
 * @param {string} employeeNo - Employee number
 * @returns {Promise<string>} Base64 encoded PDF document
 */
export async function generateP45Document(employeeNo) {
  try {
    console.log(`📄 Generating P45 for employee: ${employeeNo}`);

    // Get BC token
    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    // Construct the P45 generator endpoint
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/p45Generators('${employeeNo}')/Microsoft.NAV.GenerateP45Base64`;

    console.log('P45 Endpoint:', endpoint);

    // Call the P45 generation endpoint
    const response = await bcClient.post(endpoint, token, {});

    console.log('✅ P45 generated successfully');

    // The response should contain the base64 PDF
    // Business Central API typically returns the result in a 'value' property
    if (response && response.value) {
      return response.value;
    } else if (typeof response === 'string') {
      return response;
    } else {
      throw new Error('Invalid response format from P45 generation endpoint');
    }
  } catch (error) {
    console.error('❌ Error generating P45:', error);
    throw error;
  }
}

/**
 * Download P45 document
 * @param {string} base64Data - Base64 encoded PDF
 * @param {string} employeeNo - Employee number for filename
 */
export function downloadP45Document(base64Data, employeeNo) {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `P45_${employeeNo}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('✅ P45 document downloaded');
  } catch (error) {
    console.error('❌ Error downloading P45:', error);
    throw error;
  }
}

const p45Service = {
  generateP45Document,
  downloadP45Document,
};

export default p45Service;
